import { getBrowserInfo, getOsInfoWithoutHints as getOsInfo } from 'owa-user-agent';
import type { Browser, OperatingSystem } from 'owa-user-agent';
import type {
    BrowserVersionRange,
    OSVersionRange,
    SupportedBrowserConfig,
} from '../schema/A2HSSupportedBrowserConfig';

/**
 * Tells whether user's browser is supported for A2HS campaign based on
 * supported browsers configurations
 * @param supportedBrowserConfigs  Configurations of supported browsers
 */
export const isUserBrowserSupportedForA2HSCampaign = (
    supportedBrowserConfigs: SupportedBrowserConfig[]
): boolean => !!supportedBrowserConfigs.find(doesUserBrowserMatchSupportedBrowserConfig);

export const doesUserBrowserMatchSupportedBrowserConfig = (
    supportedBrowserConfig: SupportedBrowserConfig
): boolean => {
    const {
        browserName: supportedBrowserName,
        browserVersionRange: supportedVersionRangeForBrowser,
        operatingSystem: supportedOSForBrowser,
        operatingSystemsVersionRange: supportedVersionRangeForOS,
    } = supportedBrowserConfig;

    return (
        isUserBrowserSupported(supportedBrowserName, supportedVersionRangeForBrowser) &&
        isUserOsSupported(supportedOSForBrowser, supportedVersionRangeForOS)
    );
};

export const isUserBrowserSupported = (
    supportedBrowserName: Browser,
    supportedVersionRange: BrowserVersionRange
) => {
    const { browser: userBrowserName, browserVersion: userBrowserVersion } = getBrowserInfo();

    if (userBrowserName !== supportedBrowserName) {
        return false;
    }

    if (!supportedVersionRange?.length) {
        // All versions supported
        return true;
    }

    if (supportedVersionRange.length === 1) {
        const minimumSupportedVersion = supportedVersionRange[0];
        return isMinimumVersion(minimumSupportedVersion, userBrowserVersion);
    }

    const minimumSupportedVersion = supportedVersionRange[0];
    const minimumDeprecatedVersion = supportedVersionRange[1];

    return (
        isMinimumVersion(minimumSupportedVersion, userBrowserVersion) &&
        !isMinimumVersion(minimumDeprecatedVersion, userBrowserVersion)
    );
};

export const isUserOsSupported = (
    supportedOsName: OperatingSystem,
    supportedVersionRange: OSVersionRange
) => {
    const { os: userOS, osVersion: userOsVersionRaw } = getOsInfo();
    const userOsVersion = userOsVersionRaw?.split('.').map(Number);

    if (userOS !== supportedOsName) {
        return false;
    }

    if (!supportedVersionRange?.length) {
        // All versions supported
        return true;
    }

    if (supportedVersionRange.length === 1) {
        const minimumSupportedVersion = supportedVersionRange[0];
        return isMinimumVersion(minimumSupportedVersion, userOsVersion);
    }

    const minimumSupportedVersion = supportedVersionRange[0];
    const minimumDeprecatedVersion = supportedVersionRange[1];

    return (
        isMinimumVersion(minimumSupportedVersion, userOsVersion) &&
        !isMinimumVersion(minimumDeprecatedVersion, userOsVersion)
    );
};

export function isMinimumVersion(minimumVersion: number[], currentVersion: number[]): boolean {
    if (minimumVersion === undefined || currentVersion === undefined) {
        return false;
    }

    const maxLength = Math.max(minimumVersion.length, currentVersion.length);

    for (let index = 0; index < maxLength; index++) {
        const currentVersionPart = currentVersion[index] || 0;
        const minimumVersionPart = minimumVersion[index] || 0;

        if (currentVersionPart !== minimumVersionPart) {
            return currentVersionPart > minimumVersionPart;
        }
    }

    return true;
}
